import React from "react";
import { ConfirmationDialogComponent } from "@iventis/components";
import { MapMode } from "@iventis/map-engine";
import { checkModeIsPresent } from "@iventis/map-engine/src/machines/mode-machine.helpers";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { Feature } from "geojson";
import { eventStream } from "../event-stream";

/** Checks if line splitting should be enabled and what tooltip to display depending on selection state */
export function useLineSplittingState(selectedMapObjects: Feature<AnySupportedGeometry, MapObjectProperties>[], selectedLayers: DomainLayer[], mode: MapMode) {
    const translate = useIventisTranslate();

    // Check that only one map object is selected
    if (selectedMapObjects.length !== 1) {
        return { isSplitLineValid: false, splitLineTooltip: translate(Content.map11.splitLines.selectMapObject), isSplitting: false };
    }

    const singleMapObject = selectedMapObjects[0];
    const layer = selectedLayers.find((layer) => layer.id === singleMapObject.properties.layerid);

    // Check if the selected layer is locked
    if (layer && layer.locked) {
        return { isSplitLineValid: false, splitLineTooltip: translate(Content.map11.splitLines.lockedLayer), isSplitting: false };
    }

    // Check that the map object selected is a line
    if (singleMapObject.geometry.type !== "LineString") {
        return { isSplitLineValid: false, splitLineTooltip: translate(Content.map11.splitLines.linesOnly), isSplitting: false };
    }

    // Check if map object is a fixed shape
    if (singleMapObject.properties.fixedShape) {
        return { isSplitLineValid: false, splitLineTooltip: translate(Content.map11.splitLines.fixedShapes), isSplitting: false };
    }

    // Check if we are drawing
    if (mode !== "edit.default" && !checkModeIsPresent(mode, "edit.splitLine")) {
        return { isSplitLineValid: false, splitLineTooltip: translate(Content.map11.splitLines.drawing), isSplitting: false };
    }

    const onConfirm = () => {
        eventStream.next({ type: "CONFIRM_SPLIT" });
        incrementMonitoringCount({ name: MetricName.SPLIT_LINE, quantity: 1 });
    };

    const onCancel = () => {
        eventStream.next({ type: "FINISH" });
    };

    return {
        isSplitLineValid: true,
        splitLineTooltip: translate(Content.map11.splitLines.action),
        isSplitting: checkModeIsPresent(mode, "edit.splitLine"),
        confirmationModal: (
            <ConfirmationDialogComponent
                show={checkModeIsPresent(mode, "edit.splitLine.asking")}
                handleConfirm={onConfirm}
                handleCancel={onCancel}
                title={translate(Content.map11.splitLines.title)}
                message={translate(Content.map11.splitLines.confirmMessage)}
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.cancel)}
            />
        ),
    };
}
